import { unflatten } from 'flat';
import Validator from '../../../../components/forms/validate';
import { OVERRIDE_SCOPES } from '../../../../constants/compliance-verdicts';

/*
 * The rules are built per scope rather than expressed with validatorjs `required_if`. The scope is
 * the one thing that decides which fields exist, and stating that as a plain branch keeps the rule
 * that guards the API's 422 out of a conditional-rule implementation.
 */
const rulesFor = (scope) => {
	const rules = {
		'serialNumber': ['required'],
		'scope': ['required', `in:${Object.values(OVERRIDE_SCOPES).join(',')}`],
		'reason': ['required']
	};

	if (scope !== OVERRIDE_SCOPES.APPLIANCE) {
		rules.hardwareComponentTypeId = ['required'];
	}

	if (scope === OVERRIDE_SCOPES.COMPONENT_VERSION) {
		rules.firmwareVersion = ['required'];
	}

	return rules;
};

const CUSTOM_MESSAGES = {
	'required.serialNumber': 'Enter the appliance serial number',
	'required.reason': 'State why this appliance is being exempted',
	'required.hardwareComponentTypeId': 'Choose a component type',
	'required.firmwareVersion': 'Enter the firmware version this override covers'
};

export const validateComplianceVerdictForm = (values) => {
	const scope = values?.scope ?? OVERRIDE_SCOPES.APPLIANCE;
	const validator = new Validator(values, rulesFor(scope), CUSTOM_MESSAGES);
	const errors = validator.fails() ? unflatten(validator.errors.all()) : {};

	/*
	 * No hand-written blank guard on `serialNumber`/`reason`, deliberately. These land in NOT NULL
	 * *audit* columns and the API rejects whitespace-only values with its own post-validator - but
	 * this project's validatorjs is a Syneto fork whose `required` already rejects "   " and
	 * "\t\n", so a guard here would be unreachable. The behaviour is pinned by a test instead, so
	 * that a fork change which loosened `required` would fail rather than silently let whitespace
	 * through to an audit column.
	 */

	/*
	 * The granularity rule the API actually states, asserted independently of the scope selector.
	 * Redundant while the selector exists - it cannot express this combination - and kept because
	 * it is the rule that survives whoever replaces the selector with two optional fields.
	 */
	if (!errors.firmwareVersion
		&& String(values?.firmwareVersion ?? '').trim()
		&& !String(values?.hardwareComponentTypeId ?? '').trim()) {
		errors.firmwareVersion = 'A firmware version needs a component type; a version-scoped override without one is not supported';
	}

	return errors;
};

/*
 * Granting on a recorded verdict takes its scope from the verdict, so the reason is the only field
 * the operator supplies. It lands in the same NOT NULL audit column as a direct override's reason.
 */
export const validateComplianceVerdictGrantForm = (values) => {
	const validator = new Validator(values, { 'reason': ['required'] }, CUSTOM_MESSAGES);

	return validator.fails() ? unflatten(validator.errors.all()) : {};
};

export default validateComplianceVerdictForm;
